/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import { useColorScheme } from '../UNSAFE_useColorScheme';
import { useScale } from '../UNSAFE_useScale';
import { themeInterpolations } from '../../utils/UNSAFE_interpolations/theme';
import type { ThemeProps } from '../../utils/UNSAFE_interpolations/theme';
import { mergeInterpolations } from '../../utils/UNSAFE_mergeInterpolations';
import { DefaultEnvironment } from '../../UNSAFE_Environment/EnvironmentContext';
import { DARK_CLASS, INVERT_CLASS } from '../../UNSAFE_Theme';
import { useDensity } from '#hooks/UNSAFE_useDensity';
import { UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';

import { toUnsafeDomElement } from '#utils/PRIVATE_unsafeDomRefUtils/unsafeDomRefUtils';

/**
 * The logical parent of the re-parented content that 'launched' it in the popup/dropdown.
 */
type LogicalParentRef = RefObject<Element> | RefObject<UnsafeDomHandle<Element>> | null;

// This hook encapsulates the logic required to determine which theme style classes are needed
// for the current environment context's colorScheme, density, and scale value.  This would be used when
// rendering dom outside of the current dom tree, but desire to match the colorScheme, density, and scale
// of the original dom tree.
export function useThemeInterpolations(logicalParentRef?: LogicalParentRef) {
  const [colorScheme, setColorScheme] = useState<'light' | 'dark' | undefined>(undefined);

  const currentColorScheme = useColorScheme();
  const currentDensity = useDensity();
  const currentScale = useScale();
  // We don't want to interpolate the default environment values for scale or density. Those should get picked up by default.
  // This fixes an issue for core pack components that utilize the Layer component, when in a legacy cookbook page.
  const density = currentDensity !== DefaultEnvironment.density ? currentDensity : undefined;
  const scale = currentScale !== DefaultEnvironment.scale ? currentScale : undefined;

  // We want to use useLayoutEffect instead of useEffect here. Otherwise there could be
  // flashing due to light colorScheme initial render then dark colorScheme after useEffect.
  useLayoutEffect(() => {
    // for colorScheme in Legacy, things are a bit more complicated because unlike scale which is always applied to
    // the root dom element, we can switch to dark theme by adding the .oj-color-invert class to any dom element where
    // we want the dark theme to start.  In Legacy, you can't switch back to light theme once you switch to dark (no nesting).
    // The problem with our core pack Components that use Layer to display the dropdown, it is not in the same dom
    // tree as the parent component.  It is in its own dom tree off of the root dom element, so it won't see the
    // .oj-color-invert class in the dom tree of the select single.
    // Since in legacy .oj-color-invert can appear anywhere, not just on the root dom element, if the colorScheme
    // is the same as the default colorScheme then we need to look at the ref to determine if it or any of its
    // ancestors has the .oj-color-invert class.  If it does(and doesn't have the preact .oj-c-colorscheme-dark class),
    // use 'dark' colorScheme.
    // We only want to do this for legacy, so if the closest element that has the .oj-color-invert also has .oj-c-colorscheme-dark,
    // then we don't want to override since this isn't a legacy color change and we already handle it in our environment context
    // so the color scheme nesting will work in Preact/Core Pack.
    const logicalParent = toUnsafeDomElement(logicalParentRef);
    const closestElementWithColorInvertClass = logicalParent?.closest(
      `.${INVERT_CLASS}:not(.${DARK_CLASS})`
    );

    setColorScheme(
      currentColorScheme !== DefaultEnvironment.colorScheme
        ? currentColorScheme
        : closestElementWithColorInvertClass
        ? 'dark'
        : 'light'
    );
  }, [logicalParentRef, currentColorScheme]);

  const interpolations = mergeInterpolations<ThemeProps>([...Object.values(themeInterpolations)]);
  const { class: classes } = interpolations({ colorScheme, scale, density });
  return classes;
}
